import React from 'react'
import { useDispatch } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import FormError from '~/Common-Objects/UI/Forms/FormError/FormError'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Radio from '~/Common-Objects/UI/Forms/Radio/Radio'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import TextInputGroup from '~/Common-Objects/UI/Forms/TextInputGroup'
import UpsellChip from '~/Common-Objects/UI/Chips/Upsell.jsx'
import UpsellModal from '~/Common-Objects/UpsellModal'
import ImagePicker from '~/Common-Objects/ImagePicker'
import FileManager from '~/pages/cp/includes/fileManager/fileManager'
import { alterSiteDrawer, alterFullpage } from '~/data/actions/siteActions'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'
import i18n from '~/i18n'

const subheaderStyle = {
    fontWeight: '600',
    fontStyle: 'Semi Bold',
    fontSize: '16px',
    lineHeight: '20px'
}
const commentStyle = {
    fontWeight: '400',
    fontStyle: 'Regular',
    fontSize: '14px',
    leadingTrim: 'NONE',
    lineHeight: '20px',
    letterSpacing: ' -0.2px'
}

export default function LandingPageSettingDetails({
    landingPage,
    setValue,
    setOptions,
    saveLandingPage = () => {},
    addDomain = () => {},
    errors,
    domains,
    url,
    sendingDomains = [],
    addTrackingDomain = () => {}
}) {
    const dispatch = useDispatch()
    const { tierAllowed: customDomainAllowed, requiredTier } = useTierAllowed('landingPageCustomDomains')

    const openImagePicker = () => {
        const content = (
            <FileManager
                onFileClick={(newFile) => {
                    dispatch(alterSiteDrawer(false, true, 'right', null, true, null))
                    setOptions('favicon', newFile.url)
                }}
                fileTypes={'images'}
            />
        )
        dispatch(alterSiteDrawer(true, true, 'right', content, true, '100vw', 'full-height'))
    }

    const onAddDomainClick = () => {
        if (sendingDomains?.length === 1) {
            // assume its that one and they just need to add a tracking domain to it
            addDomain(sendingDomains[0]?.id, 'dnsInstructions')
        } else {
            addDomain(null, 'addDomain')
        }
    }

    const showCustomDomainUpsell = () => {
        dispatch(
            alterFullpage(
                <UpsellModal
                    feature='landingPageCustomDomains'
                    requiredTier={requiredTier?.name || 'Growth'}
                    onUpgrade={() => dispatch(alterSiteDrawer(false, true, 'right', null))}
                />
            )
        )
    }

    const onCustomDomainSelect = () => {
        if (!customDomainAllowed) {
            showCustomDomainUpsell()
            return
        }
        setOptions('trackingDomain', domains[0]?.id || -1)
    }

    const useCustomDomain = customDomainAllowed && !!landingPage.options?.trackingDomain

    return (
        <Paper className='paper-padding-24' style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h4>Details</h4>
            <TextInput
                label='Page Title'
                value={landingPage.options?.title}
                onChange={(e) => setOptions('title', e.target.value)}
            />
            <div>
                <div style={{ ...subheaderStyle, marginBottom: 8 }}>Page URL</div>
                <div style={commentStyle}>
                    Change the URL of your landing page or use your authenticated domains to publish landing pages to
                    your own domains.
                </div>
            </div>
            <div>
                <Radio checked={!useCustomDomain} onChange={() => setOptions('trackingDomain', null)}>
                    Use a Transpond domain
                </Radio>
                {!useCustomDomain && (
                    <TextInputGroup
                        style={{ marginTop: 8, paddingLeft: 28 }}
                        className='mar-t8'
                        error={errors?.slug}
                    >
                        <TextInput
                            label='URL'
                            value={url}
                            outerStyle={{ flexGrow: 0 }}
                            style={{ width: 250 }}
                            disabled={true}
                        />
                        <TextInput
                            value={landingPage.slug}
                            onChange={(e) => setValue('slug', e.target.value)}
                            className={errors?.slug ? 'button-warning' : ''}
                        />
                    </TextInputGroup>
                )}
            </div>

            <div>
                <Radio checked={useCustomDomain} onChange={onCustomDomainSelect}>
                    Use a custom domain
                    {!customDomainAllowed && (
                        <UpsellChip style={{ marginLeft: 8 }}>{requiredTier?.name || 'Growth'}</UpsellChip>
                    )}
                </Radio>
                {useCustomDomain && domains.length > 0 && (
                    <>
                        <TextInputGroup
                            style={{ marginTop: 8, paddingLeft: 28 }}
                            className='mar-t8'
                            error={errors?.slug || errors?.trackingDomain}
                        >
                            <Select
                                value={landingPage.options?.trackingDomain}
                                data={domains.map((d) => ({ label: d.domainName, value: d.id }))}
                                onChange={(e) => setOptions('trackingDomain', e.target.value)}
                                outerStyle={{ flexGrow: 0 }}
                                style={{ width: 250 }}
                            />
                            <TextInput
                                value='/s/'
                                disabled={true}
                                outerStyle={{ flexGrow: 0 }}
                                style={{ width: 44 }}
                                className={errors?.slug ? 'button-warning' : ''}
                            />
                            <TextInput
                                value={landingPage.slug}
                                onChange={(e) => setValue('slug', e.target.value)}
                                className={errors?.slug ? 'button-warning' : ''}
                            />
                        </TextInputGroup>
                        <div style={{ paddingLeft: 30, paddingTop: 4 }}>
                            <a onClick={addTrackingDomain}>Create a new subdomain</a>
                        </div>
                    </>
                )}
                {useCustomDomain && domains.length === 0 && (
                    <div className='quickFlex' style={{ gap: 8 }}>
                        <Button
                            className='button-primary mar-t10'
                            text={sendingDomains?.length === 1 ? 'Add a tracking domain' : 'Add a domain'}
                            onClick={onAddDomainClick}
                        />
                        {!!errors?.trackingDomain && <FormError>{errors.trackingDomain}</FormError>}
                    </div>
                )}
                {!customDomainAllowed && (
                    <div style={{ ...commentStyle, paddingLeft: 28, marginTop: 8, color: 'var(--neutral-600)' }}>
                        {i18n.t('form:landingPages.customDomainUpsell')}
                    </div>
                )}
            </div>

            <div>
                <div style={{ ...subheaderStyle, marginBottom: 8 }}>Favicon</div>
                <div style={commentStyle}>
                    Add a favicon to change the small icon that will appear in browser tabs next to your page title.
                </div>
            </div>
            <ImagePicker
                hideControls={true}
                imageUrl={landingPage.options?.favicon}
                onImageClick={openImagePicker}
                isEditable={false}
                isDynamic={false}
            />

            <div>
                <Button className='button-primary' onClick={() => saveLandingPage()}>
                    Save
                </Button>
            </div>
        </Paper>
    )
}
